import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button, TextArea } from "@progress/kendo-react-all";
import FormActionsBar from "@excent-one/react/form/layouts/FormActionsBar";
import { dialogBox } from "@excent-one/react/dialog-box";
import { notification } from "@excent-one/react/notification";
import { BadRequestError } from "@excent-one/core";
import { saveIcon, cancelIcon, pencilIcon } from "@progress/kendo-svg-icons";
import {
  cancelIncidentRecords,
  completeIncidentRecords,
} from "@phrmpc/infrastructure/phrmpc/api/incident-records";
import { getAllIncidentStatuses } from "@phrmpc/infrastructure/phrmpc/api/incident-statuses";
import type { IncidentRecordDto } from "@phrmpc/infrastructure/phrmpc/models";
import { useIncidentMonitoringGating } from "./IncidentMonitoringGatingContext";
import { confirmCaseDocumentSubmit } from "./CaseDocumentTabStrip";
import type { IncidentFormValues } from "../types";

/**
 * Status-transition actions (Cancel/Complete) call their endpoints directly rather than through
 * the page's <Form> submit pipeline, so there's no `setError` field-mapping to surface a 400's
 * actual validation messages - without this, every failure (a named business-rule violation, a
 * genuine server crash, or anything else) collapsed into the same unhelpful "please try again"
 * toast, making the real cause invisible to the user reporting the bug (Bug 157828).
 */
const getServerErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof BadRequestError) {
    const errors = e.data?.errors as Record<string, string[]> | undefined;
    const messages = errors ? Object.values(errors).flat() : [];
    if (messages.length > 0) return messages.join(" ");
    if (e.data?.title) return e.data.title;
  }
  return fallback;
};

interface StatusButtonConfig {
  showMonitoring: boolean;
  showEdit: boolean;
  showMainSave: boolean;
  showMonitorSave: boolean;
  showSubmit: boolean;
  showCancel: boolean;
}

const STATUS_BUTTON_CONFIG: Record<string, StatusButtonConfig> = {
  Draft: {
    showMonitoring: false,
    showEdit: true,
    showMainSave: true,
    showMonitorSave: false,
    showSubmit: true,
    showCancel: true,
  },
  Created: {
    showMonitoring: true,
    showEdit: false,
    showMainSave: false,
    showMonitorSave: true,
    showSubmit: false,
    showCancel: false,
  },
  "On going": {
    showMonitoring: true,
    showEdit: false,
    showMainSave: false,
    showMonitorSave: true,
    showSubmit: false,
    showCancel: false,
  },
  Cancelled: {
    showMonitoring: false,
    showEdit: false,
    showMainSave: false,
    showMonitorSave: false,
    showSubmit: false,
    showCancel: false,
  },
  Completed: {
    showMonitoring: true,
    showEdit: false,
    showMainSave: false,
    showMonitorSave: false,
    showSubmit: false,
    showCancel: false,
  },
};

export const getStatusConfig = (status: string): StatusButtonConfig =>
  STATUS_BUTTON_CONFIG[status] ?? STATUS_BUTTON_CONFIG.Draft;

export interface IncidentFormActionsProps {
  loading: boolean;
  readOnly: boolean;
  id: string | undefined;
  status: string;
  showMonitorSection: boolean;
  recordData: IncidentRecordDto | undefined;
  getValues: () => IncidentFormValues;
  /**
   * Routes Save/Submit through the <Form>'s own submit pipeline (formRef.current.submit()) rather
   * than calling an API directly from onClick - that pipeline runs RHF validation, calls the
   * page's onSubmit, and maps any thrown BadRequestError's field errors via setError. "draft" and
   * "submitIncident" pick which endpoint/status-transition that shared onSubmit performs.
   */
  triggerSubmit: (intent: "draft" | "submitIncident") => void;
  buildPayload: (
    values: IncidentFormValues,
    statusIdOverride?: number,
  ) => IncidentRecordDto;
  defaultValues: IncidentFormValues;
  isFormDirty: () => boolean;
}

/**
 * The page's FormActionsBar (Save/Submit/Cancel/Edit/Complete/Back) - owns its own statusOptions
 * lookup (only needed here, for status-transition id resolution) and the status-transition
 * callbacks (handleCancel/handleSubmitIncident/handleMonitorComplete/back) that were previously
 * defined at the page level. Reads pathAComplete/monitorCanComplete/pendingCaseDocumentSubmit via
 * IncidentMonitoringGatingContext instead of receiving them as props, since they're written by
 * whichever MonitoringCaseSection is currently active.
 */
const IncidentFormActions = ({
  loading,
  readOnly,
  id,
  status,
  showMonitorSection,
  getValues,
  triggerSubmit,
  buildPayload,
  isFormDirty,
}: IncidentFormActionsProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [gating] = useIncidentMonitoringGating();

  const statusConfig = getStatusConfig(status ?? "");
  const isCompleted = status === "Completed";

  const { data: statusOptions = [] } = useQuery({
    queryKey: ["incident-statuses"],
    queryFn: () => getAllIncidentStatuses(),
  });

  const back = useCallback(() => {
    const proceedBack = () => navigate({ to: "/incident-management" });
    if (isFormDirty()) {
      dialogBox.confirm({
        title: "Leave without saving?",
        content: (
          <>
            Are you sure you want to go back? Any unsaved changes will be lost.
          </>
        ),
        actions: {
          confirm: { text: "Yes", onClick: proceedBack },
          cancel: { text: "No" },
        },
      });
    } else {
      proceedBack();
    }
  }, [navigate, isFormDirty]);

  const handleCancel = useCallback(() => {
    // Bug 157827: the Cancel button previously called the generic `updateIncidentRecord` (PUT
    // .../{id}), which intentionally never applies StatusId/CancellationReason - status
    // transitions each have their own dedicated endpoint. That made Cancel return a misleading
    // 200/success toast while silently leaving the record on Draft. Use the dedicated
    // `cancelIncidentRecords` (PUT .../{id}/cancel) endpoint instead, which also requires a
    // non-empty cancellationReason - collected here via a plain TextArea since this dialog isn't
    // part of the react-hook-form-bound page form.
    const reasonRef = { current: "" };
    dialogBox.confirm({
      title: "Cancel this request?",
      content: (
        <>
          <p>Are you sure you want to cancel this request?</p>
          <TextArea
            placeholder="Reason for cancellation"
            rows={3}
            onChange={(e) => {
              reasonRef.current = e.value ?? "";
            }}
          />
        </>
      ),
      actions: {
        confirm: {
          text: "Yes",
          onClick: async () => {
            const cancellationReason = reasonRef.current.trim();
            if (!cancellationReason) {
              notification.error("Please provide a reason for cancellation.");
              return;
            }
            try {
              await cancelIncidentRecords(id!, {
                id: id!,
                cancellationReason,
              });
              notification.success("Incident record cancelled.");
              await queryClient.invalidateQueries({
                queryKey: ["incident-record", id],
              });
              await queryClient.invalidateQueries({
                queryKey: ["incident-records"],
              });
              navigate({ to: "/incident-management" });
            } catch (e) {
              console.error(e);
              notification.error(
                getServerErrorMessage(e, "Unable to cancel. Please try again."),
              );
            }
          },
        },
        cancel: { text: "No" },
      },
    });
  }, [id, navigate, queryClient]);

  const handleMonitorComplete = useCallback(() => {
    dialogBox.confirm({
      title: "Complete this incident?",
      content: (
        <>This will close the record and it cannot be changed. Continue?</>
      ),
      actions: {
        confirm: {
          text: "Yes",
          onClick: async () => {
            const completedStatus = statusOptions.find(
              (s) => s.name === "Completed",
            );
            try {
              // Bug 157824: this previously called the generic `updateIncidentRecord` (PUT
              // .../{id}), which intentionally never applies StatusId - status transitions each
              // have their own dedicated endpoint - so the record silently stayed on its prior
              // status despite the success toast. It also built the payload from stale
              // `initialValues` rather than live `getValues()`, discarding whatever
              // Decision/Disciplinary Action the user had just filled in. Use the dedicated
              // `completeIncidentRecords` (PUT .../{id}/complete) endpoint with live form values.
              await completeIncidentRecords(id!, {
                id: id!,
                payload: buildPayload(getValues(), completedStatus?.id),
              });
              notification.success("Incident is successfully completed");
              await queryClient.invalidateQueries({
                queryKey: ["incident-record", id],
              });
              await queryClient.invalidateQueries({
                queryKey: ["incident-records"],
              });
            } catch (e) {
              console.error(e);
              notification.error(
                getServerErrorMessage(e, "Unable to complete. Please try again."),
              );
            }
          },
        },
        cancel: { text: "No" },
      },
    });
  }, [id, statusOptions, buildPayload, getValues, queryClient]);

  return (
    <FormActionsBar loading={loading}>
      {readOnly ? (
        <>
          {statusConfig.showCancel && (
            <Button
              type="button"
              fillMode="outline"
              themeColor="error"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
          {statusConfig.showEdit && (
            <Button
              type="button"
              svgIcon={pencilIcon}
              themeColor="base"
              onClick={(e) => {
                e.preventDefault();
                navigate({
                  to: "/incident-management/$id/edit",
                  params: { id: id! },
                });
              }}
            >
              Edit
            </Button>
          )}
          {statusConfig.showSubmit && (
            <Button
              type="button"
              themeColor="primary"
              onClick={() => triggerSubmit("submitIncident")}
            >
              Submit
            </Button>
          )}
          {showMonitorSection && !isCompleted && (
            <>
              {gating.pendingCaseDocumentSubmit && (
                <Button
                  type="button"
                  themeColor="primary"
                  disabled={gating.pendingCaseDocumentSubmit.disabled}
                  onClick={() =>
                    confirmCaseDocumentSubmit(gating.pendingCaseDocumentSubmit!)
                  }
                >
                  Submit
                </Button>
              )}
              <Button
                type="button"
                themeColor="primary"
                disabled={!gating.monitorCanComplete}
                onClick={handleMonitorComplete}
              >
                Complete
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          {statusConfig.showMainSave && (
            <Button
              type="button"
              svgIcon={saveIcon}
              themeColor="primary"
              onClick={() => triggerSubmit("draft")}
            >
              Save
            </Button>
          )}
          {statusConfig.showSubmit && (
            <Button
              type="button"
              themeColor="base"
              onClick={() => triggerSubmit("submitIncident")}
            >
              Submit
            </Button>
          )}
        </>
      )}
      <Button
        type="button"
        fillMode="outline"
        svgIcon={cancelIcon}
        themeColor="secondary"
        onClick={back}
      >
        Back
      </Button>
    </FormActionsBar>
  );
};

export default IncidentFormActions;
